import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { UseFormRegister, UseFormSetValue, UseFormWatch, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { notify } from '../helper/notify';
import { convertFileToBase64 } from '../../common/FunctionUtils';
import MileageReportForm from './deduction-forms/mileage-report-form.component';
import TravelExpensesForm from './deduction-forms/travel-expenses-form.component';
import MaterialsToolsForm from './deduction-forms/materials-tools-form.component';
import AllowanceOutsideSwedenForm from './deduction-forms/allowance-outside-sweden-form.component';
import AllowanceInSwedenForm from './deduction-forms/allowance-in-sweden-form.component';
import { getDeductionTypeFromArticleNumber } from '../constants/invoice-deductions.constants'; 
import './add-deduction-modal.scss';
import apiCall from '../../api';

interface AddDeductionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  errors: FieldErrors<any>;
  trigger: UseFormTrigger<any>;
  t: (key: string) => string;
  editingDeductionData?: any | null;
  copiedInvoiceData?: any | null;
  preferences?: any;
  showClientSelector?: boolean;
  customers?: Array<{ value: string; label: string }>;
  selectedCustomerId?: number | null;
  onCustomerChange?: (customerId: number) => void;
}

// The "show PDF to customer" dropdown is bound to a 'yes'/'no' string, but the
// saved/fetched value arrives as a boolean (or 'yes'/'no'/'1'/'0'). Normalise it
// so edit-mode hydration selects the right option.
const pdfFlagToYesNo = (v: any): string => {
  if (v === true || v === 'yes' || v === '1' || v === 1) return 'yes';
  if (v === false || v === 'no' || v === '0' || v === 0) return 'no';
  return v || '';
};

const AddDeductionModal: React.FC<AddDeductionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  register,
  setValue,
  watch,
  errors,
  trigger,
  t: propT,
  copiedInvoiceData,
  editingDeductionData,
  preferences,
  showClientSelector = false,
  customers = [],
  selectedCustomerId = null,
  onCustomerChange
}) => {
  // Use translation hook
  const { t } = useTranslation();
  
  // Get VAT from preferences (default to 25 if not available)
  const vatFromPreferences = typeof preferences?.vat === 'number' ? preferences.vat : (typeof preferences?.vat === 'string' ? parseInt(preferences.vat) : 25);
  
  // Deduction Type dropdown state
  const [isDeductionTypeDropdownOpen, setIsDeductionTypeDropdownOpen] = useState(false);
  const deductionTypeDropdownRef = React.useRef<HTMLDivElement>(null);
  
  // Client dropdown state
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const clientDropdownRef = React.useRef<HTMLDivElement>(null);
  
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [total, setTotal] = useState('0.00');
  const [countries, setCountries] = useState<any[]>([]);
  const [fileError, setFileError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    deductionType: 'mileage_report',
    isTaxFree: '',
    showOnInvoice: '',
    isPdfAttachedToInvoice: 'yes',
    travelDate: '',
    distance: '',
    reason: '',
    carRegistration: '',
    tripStartPlace: '',
    tripEndPlace: '',
    fileContent: '',
    fileName: '',
    vat: ''
  });

  // Travel Expenses PDF form state
  const [travelExpensesData, setTravelExpensesData] = useState({
    showOnInvoice: '',
    isPdfAttachedToInvoice: 'yes',
    travelDate: '',
    vat: '',
    amountExVat: ''
  });

  // Materials & Tools form state
  const [materialsToolsData, setMaterialsToolsData] = useState({
    isTaxFree: '',
    showOnInvoice: '',
    isPdfAttachedToInvoice: 'yes',
    dateOfPurchase: '',
    vat: '',
    amountExVat: ''
  });

  // Allowance Outside Sweden form state
  const [allowanceOutsideSwedenData, setAllowanceOutsideSwedenData] = useState({
    isTaxFree: '',
    showOnInvoice: '',
    isPdfAttachedToInvoice: 'yes',
    tripStartDate: '',
    tripEndDate: '',
    reason: '',
    country: '',
    destination: '',
    vat: '',
    foodAllowance: 'no_food_expense'
  });

  // Allowance In Sweden form state
  const [allowanceInSwedenData, setAllowanceInSwedenData] = useState({
    isTaxFree: '',
    showOnInvoice: '',
    isPdfAttachedToInvoice: 'yes',
    isPartialWorkday: 'no',
    tripStartDate: '',
    tripEndDate: '',
    reason: '',
    country: 'Sweden',
    destination: '',
    vat: '',
    foodAllowance: 'no_food_expense'
  });

  // Reset all form data when modal opens OR populate with editing data
  useEffect(() => {
    if (isOpen) {
      if (editingDeductionData) {
        // EDIT MODE: Populate form with existing deduction data
        let deductionTypeValue = 'mileage_report'; // default fallback
        
        // Prioritize article number lookup if available (for copied invoice working hours)
        if (editingDeductionData.articleNumber) {
          deductionTypeValue = getDeductionTypeFromArticleNumber(editingDeductionData.articleNumber);
        } else if (editingDeductionData.deductionType) {
          // Use explicit deductionType if article number not available
          deductionTypeValue = editingDeductionData.deductionType;
        }
        
        // Set deduction type
        setFormData(prev => ({ ...prev, deductionType: deductionTypeValue }));
        setValue('deductionType', deductionTypeValue);
        
        // Populate based on deduction type
        if (deductionTypeValue === 'mileage_report') {
          // Use tripStartedDate if available, fallback to travelDate
          let travelDateValue = editingDeductionData.tripStartedDate || editingDeductionData.travelDate || '';
          
          // Format date to YYYY-MM-DD for HTML date input
          if (travelDateValue && travelDateValue.includes('T')) {
            travelDateValue = travelDateValue.split('T')[0];
          }
           
          setFormData({
            deductionType: deductionTypeValue,
            isTaxFree: editingDeductionData.isTaxFree || '',
            showOnInvoice: editingDeductionData.showOnInvoice || '',
            isPdfAttachedToInvoice: pdfFlagToYesNo(editingDeductionData.isPdfAttachedToInvoice),
            travelDate: travelDateValue,
            distance: editingDeductionData.distance || '',
            reason: editingDeductionData.reason || '',
            carRegistration: editingDeductionData.carRegistration || '',
            tripStartPlace: editingDeductionData.tripStartPlace || '',
            tripEndPlace: editingDeductionData.tripEndPlace || '',
            vat: editingDeductionData.vat || '',
            fileContent: editingDeductionData.fileContent || '',
            fileName: editingDeductionData.fileName || ''
          });
          setValue('isTaxFree', editingDeductionData.isTaxFree || '');
          setValue('showOnInvoice', editingDeductionData.showOnInvoice || '');
          setValue('travelDate', travelDateValue);
          setValue('distance', editingDeductionData.distance || '');
          setValue('reason', editingDeductionData.reason || '');
          setValue('carRegistration', editingDeductionData.carRegistration || '');
          setValue('tripStartPlace', editingDeductionData.tripStartPlace || '');
          setValue('tripEndPlace', editingDeductionData.tripEndPlace || '');
          setValue('vat', editingDeductionData.vat || '');
        } else if (deductionTypeValue === 'travel_expenses_pdf') {
          // Use tripStartedDate if available, fallback to travelDate
          let travelDateValue = editingDeductionData.tripStartedDate || editingDeductionData.travelDate || '';
          
          // Format date to YYYY-MM-DD for HTML date input
          if (travelDateValue && travelDateValue.includes('T')) {
            travelDateValue = travelDateValue.split('T')[0];
          }
        
          setTravelExpensesData({
            showOnInvoice: editingDeductionData.showOnInvoice || '',
            isPdfAttachedToInvoice: pdfFlagToYesNo(editingDeductionData.isPdfAttachedToInvoice),
            travelDate: travelDateValue,
            vat: editingDeductionData.vat || '',
            amountExVat: editingDeductionData.amountExVat || ''
          });
          setValue('showOnInvoice', editingDeductionData.showOnInvoice || '');
          setValue('travelDate', travelDateValue);
          setValue('vat', editingDeductionData.vat || '');
          setValue('amountExVat', editingDeductionData.amountExVat || '');
        } else if (deductionTypeValue === 'expenses_materials_tools_pdf') {
          // Use tripStartedDate if available, fallback to dateOfPurchase
          let dateOfPurchaseValue = editingDeductionData.tripStartedDate || editingDeductionData.dateOfPurchase || '';
          
          // Format date to YYYY-MM-DD for HTML date input
          if (dateOfPurchaseValue && dateOfPurchaseValue.includes('T')) {
            dateOfPurchaseValue = dateOfPurchaseValue.split('T')[0];
          }
             
          setMaterialsToolsData({
            isTaxFree: editingDeductionData.isTaxFree || '',
            showOnInvoice: editingDeductionData.showOnInvoice || '',
            isPdfAttachedToInvoice: pdfFlagToYesNo(editingDeductionData.isPdfAttachedToInvoice),
            dateOfPurchase: dateOfPurchaseValue,
            vat: editingDeductionData.vat || '',
            amountExVat: editingDeductionData.amountExVat || ''
          });
          setValue('isTaxFree', editingDeductionData.isTaxFree || '');
          setValue('showOnInvoice', editingDeductionData.showOnInvoice || '');
          setValue('dateOfPurchase', dateOfPurchaseValue);
          setValue('vat', editingDeductionData.vat || '');
          setValue('amountExVat', editingDeductionData.amountExVat || '');
        } else if (deductionTypeValue === 'allowance_outside_sweden') {
          // Use tripStartedDate/tripEndedDate from API, fallback to tripStartDate/tripEndDate
          let tripStartDateValue = editingDeductionData.tripStartedDate || editingDeductionData.tripStartDate || '';
          let tripEndDateValue = editingDeductionData.tripEndedDate || editingDeductionData.tripEndDate || '';
          
          // Format dates to YYYY-MM-DD for HTML date input
          if (tripStartDateValue && tripStartDateValue.includes('T')) {
            tripStartDateValue = tripStartDateValue.split('T')[0];
          }
          if (tripEndDateValue && tripEndDateValue.includes('T')) {
            tripEndDateValue = tripEndDateValue.split('T')[0];
          }

          // Calculate travel days from dates (same formula as calculateAllowanceOutsideSweden)
          let travelDaysFromDatesOutside = 1;
          if (tripStartDateValue && tripEndDateValue) {
            const startOutside = new Date(tripStartDateValue);
            const endOutside = new Date(tripEndDateValue);
            const diffTimeOutside = Math.abs(endOutside.getTime() - startOutside.getTime());
            travelDaysFromDatesOutside = Math.ceil(diffTimeOutside / (1000 * 60 * 60 * 24)) + 1;
          }
          const totalDaysOutside = Math.max(travelDaysFromDatesOutside, Number(editingDeductionData.totalDayOfTravels) || 0, 1);
          const validFoodKeysOutside = ['no_food_expense', 'breakfast', 'lunch_or_dinner', 'lunch_and_dinner', 'breakfast_lunch_and_dinner'];
          let resolvedFoodOutside: string;
          if (validFoodKeysOutside.includes(editingDeductionData.foodAllowance)) {
            // Already a string key (newly saved local deduction)
            resolvedFoodOutside = editingDeductionData.foodAllowance;
          } else {
            // Numeric total from API - divide by days to get per-day key
            const rawFoodOutside = Number(editingDeductionData.foodAllowance) || 0;
            const perDayFoodOutside = rawFoodOutside > 0 ? Math.round(rawFoodOutside / totalDaysOutside) : 0;
            const foodKeyOutside = getFoodAllowanceKeyFromAmount(perDayFoodOutside);
            resolvedFoodOutside = (foodKeyOutside !== 'no_food_expense' || rawFoodOutside === 0)
              ? foodKeyOutside
              : getFoodAllowanceKeyFromAmount(rawFoodOutside);
          }
          setAllowanceOutsideSwedenData({
            isTaxFree: editingDeductionData.isTaxFree || '',
            showOnInvoice: editingDeductionData.showOnInvoice || '',
            isPdfAttachedToInvoice: pdfFlagToYesNo(editingDeductionData.isPdfAttachedToInvoice),
            tripStartDate: tripStartDateValue,
            tripEndDate: tripEndDateValue,
            reason: editingDeductionData.reason || '',
            country: editingDeductionData.country || '',
            destination: editingDeductionData.destination || '',
            vat: editingDeductionData.vat || '',
            foodAllowance: resolvedFoodOutside
          });
          setValue('isTaxFree', editingDeductionData.isTaxFree || '');
          setValue('showOnInvoice', editingDeductionData.showOnInvoice || '');
          setValue('tripStartDate', tripStartDateValue);
          setValue('tripEndDate', tripEndDateValue);
          setValue('reason', editingDeductionData.reason || '');
          setValue('country', editingDeductionData.country || '');
          setValue('destination', editingDeductionData.destination || '');
          setValue('vat', editingDeductionData.vat || '');
        } else if (deductionTypeValue === 'allowance_in_sweden') {
          // Use tripStartedDate/tripEndedDate from API, fallback to tripStartDate/tripEndDate
          let tripStartDateValue = editingDeductionData.tripStartedDate || editingDeductionData.tripStartDate || '';
          let tripEndDateValue = editingDeductionData.tripEndedDate || editingDeductionData.tripEndDate || '';
          
          // Format dates to YYYY-MM-DD for HTML date input
          if (tripStartDateValue && tripStartDateValue.includes('T')) {
            tripStartDateValue = tripStartDateValue.split('T')[0];
          }
          if (tripEndDateValue && tripEndDateValue.includes('T')) {
            tripEndDateValue = tripEndDateValue.split('T')[0];
          }

          // Calculate travel days from dates (same formula as calculateAllowanceInSweden)
          let travelDaysFromDatesSweden = 1;
          if (tripStartDateValue && tripEndDateValue) {
            const startSweden = new Date(tripStartDateValue);
            const endSweden = new Date(tripEndDateValue);
            const diffTimeSweden = Math.abs(endSweden.getTime() - startSweden.getTime());
            travelDaysFromDatesSweden = Math.ceil(diffTimeSweden / (1000 * 60 * 60 * 24)) + 1;
          }
          const totalDaysSweden = Math.max(travelDaysFromDatesSweden, Number(editingDeductionData.totalDayOfTravels) || 0, 1);
          const validFoodKeysSweden = ['no_food_expense', 'breakfast', 'lunch_or_dinner', 'lunch_and_dinner', 'breakfast_lunch_and_dinner'];
          let resolvedFoodSweden: string;
          if (validFoodKeysSweden.includes(editingDeductionData.foodAllowance)) {
            // Already a string key (newly saved local deduction)
            resolvedFoodSweden = editingDeductionData.foodAllowance;
          } else {
            // Numeric total from API - divide by days to get per-day key
            const rawFoodSweden = Number(editingDeductionData.foodAllowance) || 0;
            const perDayFoodSweden = rawFoodSweden > 0 ? Math.round(rawFoodSweden / totalDaysSweden) : 0;
            const foodKeySweden = getFoodAllowanceKeyFromAmount(perDayFoodSweden);
            resolvedFoodSweden = (foodKeySweden !== 'no_food_expense' || rawFoodSweden === 0)
              ? foodKeySweden
              : getFoodAllowanceKeyFromAmount(rawFoodSweden);
          }
          setAllowanceInSwedenData({
            isTaxFree: editingDeductionData.isTaxFree || '',
            showOnInvoice: editingDeductionData.showOnInvoice || '',
            isPdfAttachedToInvoice: pdfFlagToYesNo(editingDeductionData.isPdfAttachedToInvoice),
            isPartialWorkday: editingDeductionData.isPartialWorkday || '',
            tripStartDate: tripStartDateValue,
            tripEndDate: tripEndDateValue,
            reason: editingDeductionData.reason || '',
            country: 'Sweden',
            destination: editingDeductionData.destination || '',
            vat: editingDeductionData.vat || '',
            foodAllowance: resolvedFoodSweden
          });
          setValue('isTaxFree', editingDeductionData.isTaxFree || '');
          setValue('showOnInvoice', editingDeductionData.showOnInvoice || '');
          setValue('isPartialWorkday', editingDeductionData.isPartialWorkday || '');
          setValue('tripStartDate', tripStartDateValue);
          setValue('tripEndDate', tripEndDateValue);
          setValue('reason', editingDeductionData.reason || '');
          setValue('destination', editingDeductionData.destination || '');
          setValue('vat', editingDeductionData.vat || '');
        }
        
        // Set files if they exist
        if (editingDeductionData.files && editingDeductionData.files.length > 0) {
          // Use existing File objects if available
          setSelectedFiles(editingDeductionData.files);
        } else if (editingDeductionData.fileContent && editingDeductionData.fileName) {
          // Create File object from base64 fileContent for display
          try {
            const base64Data = editingDeductionData.fileContent;
            const byteCharacters = atob(base64Data.split(',')[1] || base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const file = new File([blob], editingDeductionData.fileName, { type: 'application/pdf' });
            setSelectedFiles([file]);
              } catch (error) {
            console.error('❌ Error creating file from base64:', error);
            setSelectedFiles([]);
          }
        } else {
          setSelectedFiles([]);
        }
        
        setFileError('');
      } else {
        // ADD MODE: Reset all form states
        setSelectedFiles([]);
        setTotal('0.00');
        setFileError('');
        setFormData({
          deductionType: 'mileage_report',
          isTaxFree: '',
          showOnInvoice: '',
          isPdfAttachedToInvoice: 'yes',
          travelDate: '',
          distance: '',
          reason: '',
          carRegistration: '',
          tripStartPlace: '',
          tripEndPlace: '',
          fileContent: '',
          fileName: '',
          vat: ''
        });
        setTravelExpensesData({
          showOnInvoice: '',
          isPdfAttachedToInvoice: 'yes',
          travelDate: '',
          vat: '',
          amountExVat: ''
        });
        setMaterialsToolsData({
          isTaxFree: '',
          showOnInvoice: '',
          isPdfAttachedToInvoice: 'yes',
          dateOfPurchase: '',
          vat: '',
          amountExVat: ''
        });
        setAllowanceOutsideSwedenData({
          isTaxFree: '',
          showOnInvoice: '',
          isPdfAttachedToInvoice: 'yes',
          tripStartDate: '',
          tripEndDate: '',
          reason: '',
          country: '',
          destination: '',
          vat: '',
          foodAllowance: 'no_food_expense'
        });
        setAllowanceInSwedenData({
          isTaxFree: '',
          showOnInvoice: '',
          isPdfAttachedToInvoice: 'yes',
          isPartialWorkday: 'no',
          tripStartDate: '',
          tripEndDate: '',
          reason: '',
          country: 'Sweden',
          destination: '',
          vat: '',
          foodAllowance: 'no_food_expense'
        });

        // Reset React Hook Form fields
        const formFields = [
          'deductionType', 'isTaxFree', 'showOnInvoice', 'isPdfAttachedToInvoice',
          'travelDate', 'distance', 'reason', 'carRegistration', 'tripStartPlace',
          'tripEndPlace', 'vat', 'amountExVat', 'dateOfPurchase', 'tripStartDate',
          'tripEndDate', 'country', 'destination', 'isPartialWorkday'
        ];
        
        formFields.forEach(field => {
          if (field === 'deductionType') {
            setValue(field, 'mileage_report');
          } else if (field === 'country') {
            setValue(field, '');
          } else if (field === 'isPartialWorkday') {
            setValue(field, 'no');
          } else {
            setValue(field, '');
          }
        });
      }
    }
  }, [isOpen, editingDeductionData, setValue]);

  // Calculate total with VAT for travel expenses - uses watch for real-time updates
  const calculateTravelExpensesTotal = () => {
    const amount = parseFloat(watch('amountExVat')) || 0;
    const vatRate = parseFloat(watch('vat')) || 0;
    const vatAmount = (amount * vatRate) / 100;
    const totalWithVat = amount + vatAmount;
    return totalWithVat.toFixed(2);
  };

  // Calculate total with VAT for materials & tools - uses watch for real-time updates
  const calculateMaterialsToolsTotal = () => {
    const amount = parseFloat(watch('amountExVat')) || 0;
    const vatRate = parseFloat(watch('vat')) || 0;
    const vatAmount = (amount * vatRate) / 100;
    const totalWithVat = amount + vatAmount;
    return totalWithVat.toFixed(2);
  };

  // Calculate travel days and total for Allowance Outside Sweden
  const calculateAllowanceOutsideSweden = () => {
    const startDate = allowanceOutsideSwedenData.tripStartDate;
    const endDate = allowanceOutsideSwedenData.tripEndDate;
    
    if (!startDate || !endDate) {
      return { travelDays: 0, allowancePerDay: '0.00', total: '0.00' };
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end day
    
    // Get allowance per day from selected country
    const countryData = countries.find(
      (item: any) => item?.landellerOmråde === allowanceOutsideSwedenData.country
    );
    
    const allowancePerDay = Number(countryData?.normalbelopp) || 450.00;
    const total = (diffDays * allowancePerDay).toFixed(2);
    
    return { 
      travelDays: diffDays, 
      allowancePerDay: allowancePerDay.toFixed(2), 
      total 
    };
  };

  // Calculate travel days and total for Allowance In Sweden
  const calculateAllowanceInSweden = () => {
    const startDate = allowanceInSwedenData.tripStartDate;
    const endDate = allowanceInSwedenData.tripEndDate;
    
    if (!startDate || !endDate) {
      return { travelDays: 0, allowancePerDay: '150.00', total: '0.00' };
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end day
    
    // Check if partial workday is selected - if yes, use 150 SEK (half day), otherwise use 300 SEK (full day)
    const isPartialWorkday = allowanceInSwedenData.isPartialWorkday === 'yes';
    const allowancePerDay = isPartialWorkday ? 150.00 : 300.00;
    const total = (diffDays * allowancePerDay).toFixed(2);
    
    return { 
      travelDays: diffDays, 
      allowancePerDay: allowancePerDay.toFixed(2), 
      total 
    };
  };

  // Convert numeric foodAllowance amount (from API) back to string key for dropdown
  const getFoodAllowanceKeyFromAmount = (value: any): string => {
    if (value === undefined || value === null || value === '') return 'no_food_expense';
    const validKeys = ['no_food_expense', 'breakfast', 'lunch_or_dinner', 'lunch_and_dinner', 'breakfast_lunch_and_dinner'];
    if (typeof value === 'string' && validKeys.includes(value)) return value;
    const map: Record<number, string> = {
      0: 'no_food_expense',
      60: 'breakfast',
      105: 'lunch_or_dinner',
      210: 'lunch_and_dinner',
      270: 'breakfast_lunch_and_dinner',
    };
    const numAmount = parseFloat(value);
    return map[numAmount] ?? 'no_food_expense';
  };

  // Convert food allowance string key to per-day numeric amount
  const getFoodAllowanceAmountFromKey = (key: string): number => {
    const map: Record<string, number> = {
      no_food_expense: 0,
      breakfast: 60,
      lunch_or_dinner: 105,
      lunch_and_dinner: 210,
      breakfast_lunch_and_dinner: 270,
    };
    return map[key] ?? 0;
  };

  // Fetch countries list from API
  useEffect(() => {
    const getAllowance = async () => {
      try {
        const { data } = await apiCall.get('/invoice/foreign-amount-allowance');
        setCountries(data?.response?.results || []);
      } catch (err) {
        console.error('Error fetching allowance data:', err);
        setCountries([]);
      }
    };
    
    if (isOpen) {
      getAllowance();
    }
  }, [isOpen]);

  // Watch all form values
  const formValues = watch();

  // Memoized form values (converted from watch)
  const deductionType = useMemo(() => formValues.deductionType || formData.deductionType, [formValues.deductionType, formData.deductionType]);
  const isTaxFree = useMemo(() => formValues.isTaxFree || formData.isTaxFree || materialsToolsData.isTaxFree || allowanceOutsideSwedenData.isTaxFree || allowanceInSwedenData.isTaxFree, [formValues.isTaxFree, formData.isTaxFree, materialsToolsData.isTaxFree, allowanceOutsideSwedenData.isTaxFree, allowanceInSwedenData.isTaxFree]);
  const showOnInvoice = useMemo(() => formValues.showOnInvoice || formData.showOnInvoice, [formValues.showOnInvoice, formData.showOnInvoice]);
  const travelDate = useMemo(() => formValues.travelDate || formData.travelDate, [formValues.travelDate, formData.travelDate]);
  const distance = useMemo(() => formValues.distance || formData.distance, [formValues.distance, formData.distance]);
  const reason = useMemo(() => formValues.reason || formData.reason, [formValues.reason, formData.reason]);
  const carRegistration = useMemo(() => formValues.carRegistration || formData.carRegistration, [formValues.carRegistration, formData.carRegistration]);
  const tripStartPlace = useMemo(() => formValues.tripStartPlace || formData.tripStartPlace, [formValues.tripStartPlace, formData.tripStartPlace]);
  const tripEndPlace = useMemo(() => formValues.tripEndPlace || formData.tripEndPlace, [formValues.tripEndPlace, formData.tripEndPlace]);

  const deductionTypes = [
    { value: 'mileage_report', label: t('MILEAGE_REPORT') },
    { value: 'travel_expenses_pdf', label: t('TRAVEL_EXPENSES_PDF') },
    { value: 'expenses_materials_tools_pdf', label: t('EXPENSES_MATERIALS_TOOLS_PDF') },
    { value: 'allowance_outside_sweden', label: t('ALLOWANCE_OUTSIDE_SWEDEN') },
    { value: 'allowance_in_sweden', label: t('ALLOWANCE_IN_SWEDEN') }
  ];

  // Get deduction type label from value
  const getDeductionTypeLabel = (value: string) => {
    const option = deductionTypes.find(type => type.value === value);
    return option?.label || '';
  };

  // Handle click outside to close deduction type dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (deductionTypeDropdownRef.current && !deductionTypeDropdownRef.current.contains(event.target as Node)) {
        setIsDeductionTypeDropdownOpen(false);
      }
    };

    if (isDeductionTypeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDeductionTypeDropdownOpen]);

  // Handle click outside to close client dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setIsClientDropdownOpen(false);
      }
    };

    if (isClientDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClientDropdownOpen]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    const file = files[0]; // Only take first file for single upload
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    // Validate file
    if (deductionType === 'mileage_report') {
      if (file.type !== 'application/pdf') {
        notify.error(String(t('UPLOAD_ONLY_PDF')));
        return;
      }
    }
    
    if (file.size > maxSize) {
      notify.error(String(t('FILE_SIZE_LESS_THAN_1MB')));
      return;
    }
    
    setSelectedFiles([file]); // Replace existing file with new one
    setFileError(''); // Clear file error when file is uploaded
    
    // Set isPdfAttachedToInvoice to 'yes' by default when file is uploaded
    const defaultValue = 'yes';
    setValue('isPdfAttachedToInvoice', defaultValue);
    
    // Update state based on deduction type
    if (deductionType === 'mileage_report') {
      setFormData(prev => ({ ...prev, isPdfAttachedToInvoice: defaultValue }));
    } else if (deductionType === 'travel_expenses_pdf') {
      setTravelExpensesData(prev => ({ ...prev, isPdfAttachedToInvoice: defaultValue }));
    } else if (deductionType === 'expenses_materials_tools_pdf') {
      setMaterialsToolsData(prev => ({ ...prev, isPdfAttachedToInvoice: defaultValue }));
    } else if (deductionType === 'allowance_outside_sweden') {
      setAllowanceOutsideSwedenData(prev => ({ ...prev, isPdfAttachedToInvoice: defaultValue }));
    } else if (deductionType === 'allowance_in_sweden') {
      setAllowanceInSwedenData(prev => ({ ...prev, isPdfAttachedToInvoice: defaultValue }));
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFileError(''); // Clear error when file is removed
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValue(field, value);
  };

  const handleTravelExpensesChange = (field: string, value: any) => {
    setTravelExpensesData(prev => ({ ...prev, [field]: value }));
    setValue(field, value);
  };

  const handleMaterialsToolsChange = (field: string, value: any) => {
    setMaterialsToolsData(prev => ({ ...prev, [field]: value }));
    setValue(field, value);
  };

  const handleAllowanceOutsideSwedenChange = (field: string, value: any) => {
    setAllowanceOutsideSwedenData(prev => ({ ...prev, [field]: value }));
    setValue(field, value);
  };

  const handleAllowanceInSwedenChange = (field: string, value: any) => {
    setAllowanceInSwedenData(prev => ({ ...prev, [field]: value }));
    setValue(field, value);
  };

  const handleSubmit = async () => {
    
    // Validate client selection if showClientSelector is true
    if (showClientSelector && !selectedCustomerId) {
      notify.error(t('CLIENT_REQUIRED'));
      return;
    }
    
    // Determine which fields to validate based on deduction type
    let fieldsToValidate: string[] = [];
    
    if (deductionType === 'mileage_report') {
      fieldsToValidate = ['isTaxFree', 'travelDate', 'distance', 'reason', 'carRegistration', 'tripStartPlace'];
    } else if (deductionType === 'travel_expenses_pdf') {
      fieldsToValidate = ['travelDate', 'vat', 'amountExVat'];
    } else if (deductionType === 'expenses_materials_tools_pdf') {
      fieldsToValidate = ['isTaxFree', 'dateOfPurchase', 'vat', 'amountExVat'];
    } else if (deductionType === 'allowance_outside_sweden') {
      fieldsToValidate = ['isTaxFree', 'tripStartDate', 'tripEndDate', 'reason', 'country', 'destination'];
    } else if (deductionType === 'allowance_in_sweden') {
      fieldsToValidate = ['isTaxFree', 'isPartialWorkday', 'tripStartDate', 'tripEndDate', 'reason', 'destination'];
    }
    
    
    // "Is it tax-free?" is a custom dropdown (not a native registered input), so it
    // has no validation rule by default and an empty selection would pass. Attach a
    // required rule here for the types that show it, so trigger blocks Save and the
    // inline error (errors.isTaxFree) is shown. Message is translated.
    const needsTaxFree = ['mileage_report', 'expenses_materials_tools_pdf', 'allowance_outside_sweden', 'allowance_in_sweden'].includes(deductionType);
    if (needsTaxFree) {
      register('isTaxFree', {
        required: (t('PLEASE_SELECT_TAX_FREE_OPTION') as string) || 'Please select whether this is tax-free',
      });
    }

    // Trigger validation for specific fields only
    const isValid = fieldsToValidate.length > 0 ? await trigger(fieldsToValidate) : true;

    // If validation fails, stop submission
    if (!isValid) {
      return;
    }
     
    // File validation — PDF is REQUIRED for every deduction type EXCEPT the two
    // allowances (Allowance In/Outside Sweden). i.e. required for mileage_report,
    // travel_expenses_pdf and expenses_materials_tools_pdf.
    const requiresFile = ['mileage_report', 'travel_expenses_pdf', 'expenses_materials_tools_pdf'].includes(deductionType);
    if (requiresFile && selectedFiles.length === 0) {
      setFileError(t('PDF_FILE_REQUIRED') || 'PDF file is required');
      return;
    }
    // Convert files to base64
    let fileContent = " ";
    let fileName = "";
    
    if (selectedFiles.length > 0) {
      try {
        const file = selectedFiles[0];
        fileContent = await convertFileToBase64(file);
        fileName = file.name;
           } catch (error) {
        console.error('❌ Error converting file to base64:', error);
        notify.error(String(t('FILE_UPLOAD_ERROR') || 'Error uploading file. Please try again.'));
        return;
      }
    }

    let submitData;
    
    if (deductionType === 'travel_expenses_pdf') {
      submitData = {
        deductionType,
        ...travelExpensesData,
        files: selectedFiles,
        fileContent,
        fileName
      };
    } else if (deductionType === 'expenses_materials_tools_pdf') {
      submitData = {
        deductionType,
        ...materialsToolsData,
        files: selectedFiles,
        fileContent,
        fileName
      };
    } else if (deductionType === 'allowance_outside_sweden') {
      const { allowancePerDay } = calculateAllowanceOutsideSweden();
      submitData = {
        deductionType,
        ...allowanceOutsideSwedenData,
        allowancePerDay,
        vat: allowanceOutsideSwedenData.vat || vatFromPreferences, // Use VAT from form data (edit mode) or preferences (add mode)
        files: selectedFiles,
        fileContent,
        fileName
      };
    } else if (deductionType === 'allowance_in_sweden') {
      const { allowancePerDay } = calculateAllowanceInSweden();
      submitData = {
        deductionType,
        ...allowanceInSwedenData,
        allowancePerDay,
        vat: allowanceInSwedenData.vat || vatFromPreferences, // Use VAT from form data (edit mode) or preferences (add mode)
        files: selectedFiles,
        fileContent,
        fileName
      };
    } else {
      // Mileage Report
      submitData = {
        ...formData,
        deductionType,
        isTaxFree,
        showOnInvoice,
        travelDate,
        distance,
        reason,
        carRegistration,
        tripStartPlace,
        tripEndPlace,
        vat: formData.vat || vatFromPreferences, // Use VAT from form data (edit mode) or preferences (add mode)
        files: selectedFiles,
        fileContent,
        fileName,
        total
      };
    }

    // Preserve id and timeSheetWorkingHourId from the original editing data so that
    // useDeductionSubmit / transformDeductionToWorkingHour can update the existing
    // record (in both edit-invoice mode and timesheet mode) instead of creating new rows.
    if (editingDeductionData?.id) {
      submitData.id = editingDeductionData.id;
    }
    if (editingDeductionData?.timeSheetWorkingHourId) {
      submitData.timeSheetWorkingHourId = editingDeductionData.timeSheetWorkingHourId;
    }

    try {
      setIsSubmitting(true);
      // Wait for API call to complete
      await onSubmit(submitData);
      // Only close modal if API call succeeded
      onClose();
    } catch (error) {
      console.error('Failed to submit deduction:', error);
      // Modal stays open on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateTotal = () => {
    // Simple calculation based on distance (example: 2.50 Kr per km)
    const distanceValue = parseFloat(distance) || 0;
    const calculatedTotal = (distanceValue * 2.5).toFixed(2);
    setTotal(calculatedTotal);
  };

  React.useEffect(() => {
    calculateTotal();
  }, [distance]);

  // Reset form when deduction type changes (only in ADD mode, not EDIT mode)
  React.useEffect(() => {
    if (deductionType && !editingDeductionData) {
      setSelectedFiles([]);
      setFileError('');
      setFormData({
        deductionType,
        isTaxFree: '',
        showOnInvoice: '',
        isPdfAttachedToInvoice: 'yes',
        travelDate: '',
        distance: '',
        reason: '',
        carRegistration: '',
        tripStartPlace: '',
        tripEndPlace: '',
        fileContent: '',
        fileName: '',
        vat: ''
      });
      setTravelExpensesData({
        showOnInvoice: '',
        isPdfAttachedToInvoice: 'yes',
        travelDate: '',
        vat: '',
        amountExVat: ''
      });
      setMaterialsToolsData({
        isTaxFree: '',
        showOnInvoice: '',
        isPdfAttachedToInvoice: 'yes',
        dateOfPurchase: '',
        vat: '',
        amountExVat: ''
      });
      setAllowanceOutsideSwedenData({
        isTaxFree: '',
        showOnInvoice: '',
        isPdfAttachedToInvoice: 'yes',
        tripStartDate: '',
        tripEndDate: '',
        reason: '',
        country: '',
        destination: '',
        vat: '',
        foodAllowance: 'no_food_expense'
      });
      setAllowanceInSwedenData({
        isTaxFree: '',
        showOnInvoice: '',
        isPdfAttachedToInvoice: 'yes',
        isPartialWorkday: 'no',
        tripStartDate: '',
        tripEndDate: '',
        reason: '',
        country: 'Sweden',
        destination: '',
        vat: '',
        foodAllowance: 'no_food_expense'
      });

      // Reset React Hook Form fields
      const formFields = [
        'isTaxFree', 'showOnInvoice', 'isPdfAttachedToInvoice',
        'travelDate', 'distance', 'reason', 'carRegistration', 'tripStartPlace',
        'tripEndPlace', 'vat', 'amountExVat', 'dateOfPurchase', 'tripStartDate',
        'tripEndDate', 'country', 'destination', 'isPartialWorkday'
      ];
      
      formFields.forEach(field => {
        if (field === 'isPartialWorkday') {
          setValue(field, 'no');
        } else {
          setValue(field, '');
        }
      });
    }
  }, [deductionType, setValue, editingDeductionData]);

  // Render form based on deduction type
  const renderFormFields = () => {
    switch (deductionType) {
      case 'mileage_report':
        return (
          <MileageReportForm
            register={register}
            errors={errors}
            trigger={trigger}
            setValue={setValue}
            isTaxFree={isTaxFree}
            selectedFiles={selectedFiles}
            total={total}
            fileError={fileError}
            onFormChange={handleFormChange}
            onFileUpload={handleFileUpload}
            onRemoveFile={removeFile}
            formData={formData}
            vatFromPreferences={vatFromPreferences}
          />
        );

      case 'travel_expenses_pdf':
        return (
          <TravelExpensesForm
            register={register}
            errors={errors}
            trigger={trigger}
            setValue={setValue}
            selectedFiles={selectedFiles}
            totalWithVat={calculateTravelExpensesTotal()}
            fileError={fileError}
            onFormChange={handleTravelExpensesChange}
            onFileUpload={handleFileUpload}
            onRemoveFile={removeFile}
            formData={travelExpensesData}
          />
        );

      case 'expenses_materials_tools_pdf':
        return (
          <MaterialsToolsForm
            register={register}
            errors={errors}
            trigger={trigger}
            setValue={setValue}
            isTaxFree={isTaxFree}
            selectedFiles={selectedFiles}
            totalWithVat={calculateMaterialsToolsTotal()}
            fileError={fileError}
            onFormChange={handleMaterialsToolsChange}
            onFileUpload={handleFileUpload}
            onRemoveFile={removeFile}
            formData={materialsToolsData}
          />
        );

      case 'allowance_outside_sweden':
        const { travelDays, allowancePerDay, total: allowanceTotal } = calculateAllowanceOutsideSweden();
        return (
          <AllowanceOutsideSwedenForm
            register={register}
            errors={errors}
            trigger={trigger}
            setValue={setValue}
            isTaxFree={isTaxFree}
            selectedFiles={selectedFiles}
            countries={countries}
            travelDays={travelDays}
            allowancePerDay={allowancePerDay}
            total={allowanceTotal}
            tripStartDate={allowanceOutsideSwedenData.tripStartDate}
            tripEndDate={allowanceOutsideSwedenData.tripEndDate}
            fileError={fileError}
            onFormChange={handleAllowanceOutsideSwedenChange}
            onFileUpload={handleFileUpload}
            onRemoveFile={removeFile}
            formData={allowanceOutsideSwedenData}
            vatFromPreferences={vatFromPreferences}
          />
        );

      case 'allowance_in_sweden':
        const { travelDays: travelDaysSweden, allowancePerDay: allowancePerDaySweden, total: allowanceTotalSweden } = calculateAllowanceInSweden();
        return (
          <AllowanceInSwedenForm
            register={register}
            errors={errors}
            trigger={trigger}
            setValue={setValue}
            isTaxFree={isTaxFree}
            selectedFiles={selectedFiles}
            travelDays={travelDaysSweden}
            allowancePerDay={allowancePerDaySweden}
            total={allowanceTotalSweden}
            tripStartDate={allowanceInSwedenData.tripStartDate}
            tripEndDate={allowanceInSwedenData.tripEndDate}
            fileError={fileError}
            onFormChange={handleAllowanceInSwedenChange}
            onFileUpload={handleFileUpload}
            onRemoveFile={removeFile}
            formData={allowanceInSwedenData}
            vatFromPreferences={vatFromPreferences}
          />
        );

      default:
        return (
          <div className="form-group">
            <p>{t('PLEASE_SELECT_DEDUCTION_CONTINUE')}</p>
          </div>
        );
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} className="add-deduction-modal add-deduction-modal_style" centered >
      <Modal.Header className="modal-header">
        <Modal.Title className="modal-title">{t('ADD_DEDUCTION')}</Modal.Title>
        <button className="close-btn" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </Modal.Header>

      <Modal.Body className="modal-body">
        {/* Client Selector - Conditional */}
        {showClientSelector && (
          <div className="form-group">
            <label>{t('SELECT_CLIENT')}</label>
            <div className="custom-dropdown" ref={clientDropdownRef} style={{ position: 'relative' }}>
              <div
                className={`dropdown-trigger ${isClientDropdownOpen ? 'open' : ''}`}
                onClick={() => setIsClientDropdownOpen(!isClientDropdownOpen)}
                style={{
                  position: 'relative',
                  height: '46px',
                  border: `1px solid ${!selectedCustomerId && errors.customerId ? '#dc3545' : (isClientDropdownOpen ? '#7c3aed' : '#eaebf0')}`,
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: isClientDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none'
                }}
              >
                <span style={{
                  flex: 1,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: '15px',
                  fontWeight: 500,
                  lineHeight: '22px',
                  color: selectedCustomerId ? '#252525' : '#68727d'
                }}>
                  {selectedCustomerId 
                    ? customers.find(c => Number(c.value) === selectedCustomerId)?.label || t('SELECT_CLIENT')
                    : t('SELECT_CLIENT')}
                </span>
                <svg
                  className={`dropdown-arrow ${isClientDropdownOpen ? 'rotated' : ''}`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{
                    flexShrink: 0,
                    color: '#68727d',
                    transition: 'transform 0.2s ease',
                    transform: isClientDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {isClientDropdownOpen && (
                <div
                  className="dropdown-list"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    width: '100%',
                    backgroundColor: '#ffffff',
                    border: '1px solid #eaebf0',
                    borderRadius: '6px',
                    padding: '4px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}
                >
                  <div className="dropdown-content">
                    {customers.map((customer) => (
                      <div
                        key={customer.value}
                        className={`list-item ${Number(customer.value) === selectedCustomerId ? 'selected' : ''}`}
                        onClick={() => {
                          if (onCustomerChange) {
                            onCustomerChange(Number(customer.value));
                          }
                          setIsClientDropdownOpen(false);
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s ease',
                          backgroundColor: Number(customer.value) === selectedCustomerId ? '#f4efff' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4efff'}
                        onMouseLeave={(e) => {
                          if (Number(customer.value) !== selectedCustomerId) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <div className="text-input">
                          <div className="paragraph-container">
                            <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                              {customer.label}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {!selectedCustomerId && errors.customerId && (
              <div className="error-message" style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                {t('CLIENT_REQUIRED')}
              </div>
            )}
          </div>
        )}
        
        {/* Deduction Type - Single Field */}
        <div className="form-group">
          <label>{t('SELECT_DEDUCTION_TYPE')}</label>
          <div className="custom-dropdown" ref={deductionTypeDropdownRef} style={{ position: 'relative' }}>
            <div
              className={`dropdown-trigger ${isDeductionTypeDropdownOpen ? 'open' : ''}`}
              onClick={() => !editingDeductionData && setIsDeductionTypeDropdownOpen(!isDeductionTypeDropdownOpen)}
              style={{
                position: 'relative',
                height: '46px',
                border: `1px solid ${errors.deductionType ? '#dc3545' : (isDeductionTypeDropdownOpen ? '#7c3aed' : '#eaebf0')}`,
                borderRadius: '6px',
                backgroundColor: editingDeductionData ? '#f5f5f5' : '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                cursor: editingDeductionData ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: isDeductionTypeDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none'
              }}
            >
              <span style={{
                flex: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: '22px',
                color: deductionType ? '#252525' : '#68727d'
              }}>
                {deductionType ? getDeductionTypeLabel(deductionType) : t('SELECT')}
              </span>
              <svg
                className={`dropdown-arrow ${isDeductionTypeDropdownOpen ? 'rotated' : ''}`}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  flexShrink: 0,
                  color: '#68727d',
                  transition: 'transform 0.2s ease',
                  transform: isDeductionTypeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {isDeductionTypeDropdownOpen && !editingDeductionData && (
              <div
                className="dropdown-list"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #eaebf0',
                  borderRadius: '6px',
                  padding: '4px',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}
              >
                <div className="dropdown-content">
                  {deductionTypes.map((option) => (
                    <div
                      key={option.value}
                      className={`list-item ${deductionType === option.value ? 'selected' : ''}`}
                      onClick={() => {
                        handleFormChange('deductionType', option.value);
                        setIsDeductionTypeDropdownOpen(false);
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s ease',
                        backgroundColor: deductionType === option.value ? '#f4efff' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4efff'}
                      onMouseLeave={(e) => {
                        if (deductionType !== option.value) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div className="text-input">
                        <div className="paragraph-container">
                          <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                            {option.label}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {errors.deductionType && (
            <div className="error-message">{errors.deductionType.message?.toString()}</div>
          )}
        </div>

        {/* Dynamic Form Fields Based on Deduction Type */}
        {renderFormFields()}
      </Modal.Body>

      <Modal.Footer className="modal-footer">
        <div className="footer-buttons">
          <button className="cancel-btn" onClick={onClose} disabled={isSubmitting}>
            {t('CANCEL')}
          </button>
          <button className="submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('SUBMITTING') || 'Submitting...'}
              </>
            ) : (
              t('SUBMITS')
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddDeductionModal;